<script setup lang="ts">
import type { ChatMessage } from '@/api/ChatApi';
import { computed } from 'vue';
import { formatDate } from '@/utils/date';
import { marked } from 'marked';

marked.setOptions({
  async: false,
  breaks: true,
  gfm: true
});

import AiAdviserChatBubble from './AiAdviserChatBubble.vue';
import MessageDateSeperator from '@/components/sms/MessageDateSeperator.vue';
import RichContent from '@/components/richTextInput/RichContent.vue';

import IconThumbsUpLine from '@/icons/line/thumbs-up.svg';
import IconThumbsUpSolid from '@/icons/solid/thumbs-up.svg';
import IconThumbsDownLine from '@/icons/line/thumbs-down.svg';
import IconThumbsDownSolid from '@/icons/solid/thumbs-down.svg';
import LoadingDots from '@/icons/line/loading-dots.svg';

const props = withDefaults(
  defineProps<{
    messages: ChatMessage[];
    isUserMessageClamped?: boolean;
    showDateTime?: boolean;
    isLoading?: boolean;
  }>(),
  {
    showDateTime: true,
    isLoading: false
  }
);

const emit = defineEmits<{
  rate: [message: ChatMessage, rating: number];
}>();

interface MessageGroup {
  date: string;
  formattedDate: string;
  messages: ChatMessage[];
}

const groupedMessages = computed(() => {
  const groups: MessageGroup[] = [];

  props.messages.forEach((message) => {
    let dateKey: string;
    let formattedDate: string;

    if (message.created_at) {
      const messageDate = new Date(message.created_at);
      dateKey = messageDate.toDateString();
      formattedDate = formatMessageDate(messageDate);
    } else {
      const today = new Date();
      dateKey = today.toDateString();
      formattedDate = 'Today';
    }

    let group = groups.find((g) => g.date === dateKey);

    if (!group) {
      group = {
        date: dateKey,
        formattedDate,
        messages: []
      };
      groups.push(group);
    }

    group.messages.push(message);
  });

  return groups;
});

function formatMessageDate(date: Date): string {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const messageDate = new Date(date);
  messageDate.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  yesterday.setHours(0, 0, 0, 0);

  if (messageDate.getTime() === today.getTime()) {
    return 'Today';
  } else if (messageDate.getTime() === yesterday.getTime()) {
    return 'Yesterday';
  } else {
    return formatDate(date) || '';
  }
}

function getContent(message: ChatMessage) {
  const htmlContent =
    message.content_blocks?.[0]?.content?.html || message.contentBlocks?.[0]?.content?.html;

  if (htmlContent) {
    return htmlContent;
  }

  const textContent =
    message.content ||
    message.content_blocks?.[0]?.content?.text ||
    message.contentBlocks?.[0]?.content?.text;

  if (textContent) {
    return marked(textContent) as string;
  }

  return null;
}

function handleRating(message: ChatMessage, rating: number) {
  emit('rate', message, rating);
}
</script>

<template>
  <div class="flex flex-col justify-start gap-1.5">
    <div v-for="group in groupedMessages" :key="group.date">
      <MessageDateSeperator :date="group.formattedDate" />
      <div
        v-for="message in group.messages"
        :class="{
          'ml-auto pl-20 pr-5': message.type === 'user',
          'self-start px-5': message.type === 'assistant'
        }"
        :key="message.id"
        class="mb-1.5"
      >
        <AiAdviserChatBubble
          v-if="message.type === 'user'"
          :message="message"
          :showDateTime="message.type === 'user' && showDateTime"
          :isUserMessageClamped="isUserMessageClamped"
        />
        <div
          v-else
          :class="{
            'px-1 pb-4 pt-5 text-slate-700': message.type === 'assistant'
          }"
          class="flex flex-col items-stretch gap-y-3 text-md font-medium leading-normal"
        >
          <RichContent
            v-if="getContent(message)"
            :html="getContent(message)!"
            :styleLinks="false"
          />
          <div
            v-if="message.type === 'assistant' && message.id"
            class="flex items-center justify-start gap-x-5 text-slate-700"
          >
            <button
              class="transition-color px-1.5 duration-150 ease-in-out hover:text-slate-500"
              @click="handleRating(message, 1)"
            >
              <Component
                :is="message.rating === 1 ? IconThumbsUpSolid : IconThumbsUpLine"
                class="h-4 w-4"
              />
            </button>
            <button
              class="transition-color p-1.5 duration-150 ease-in-out hover:text-slate-500"
              @click="handleRating(message, -1)"
            >
              <Component
                :is="message.rating === -1 ? IconThumbsDownSolid : IconThumbsDownLine"
                class="h-4 w-4"
              />
            </button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="isLoading" class="mb-1.5 self-start py-5 pl-6">
      <LoadingDots class="w-8 text-slate-300" />
    </div>
  </div>
</template>
